import { useEffect, useMemo, useState } from 'react';
import type { Ace, MemberPublic, MemberWithStats, PostEnriched } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';
import { db } from '@/lib/db';
import { notifyFollowingChanged } from '@/lib/following-events';

type SpotlightReason = 'ACE' | 'NEW' | 'HOT' | 'STREAK' | 'GRINDER' | 'FEATURED';

interface Props {
  members: MemberPublic[];
  posts: PostEnriched[];
  aces: Ace[];
  me: MemberWithStats;
  following: string[];
  courseNames: Record<string, string>;
  onOpenProfile: (id: string) => void;
}

const REASON_CONFIG: Record<SpotlightReason, { badge: string; accent: string; bgTint: string; stamp: string }> = {
  ACE: { badge: '🦅 ACE THIS WEEK', accent: '#d4af37', bgTint: 'rgba(212,175,55,0.06)', stamp: '🦅' },
  NEW: { badge: '✨ NEW MEMBER', accent: '#c4a662', bgTint: 'rgba(196,166,98,0.08)', stamp: '✨' },
  HOT: { badge: '🔥 HOT THIS WEEK', accent: '#c8512a', bgTint: 'rgba(200,81,42,0.05)', stamp: '🔥' },
  STREAK: { badge: '🏆 ON A STREAK', accent: '#0a1e3f', bgTint: 'rgba(10,30,63,0.05)', stamp: '🏆' },
  GRINDER: { badge: '⛳ GRINDER', accent: '#8a6d3d', bgTint: 'rgba(138,109,61,0.06)', stamp: '⛳' },
  FEATURED: { badge: '★ FEATURED MEMBER', accent: '#c4a662', bgTint: 'rgba(196,166,98,0.06)', stamp: '★' },
};

function pickSpotlight(
  members: MemberPublic[],
  posts: PostEnriched[],
  aces: Ace[],
  meId: string,
  courseNames: Record<string, string>,
): { member: MemberPublic; reason: SpotlightReason; headline: string; subline: string } | null {
  const eligible = members.filter((m) => m.id !== meId);
  if (!eligible.length) return null;

  const now = Date.now();
  const weekAgo = now - 7 * 86400000;
  const twoWeeksAgo = now - 14 * 86400000;

  const recentAce = [...aces]
    .filter((a) => new Date(a.played_at).getTime() >= twoWeeksAgo && a.member_id !== meId)
    .sort((a, b) => new Date(b.played_at).getTime() - new Date(a.played_at).getTime())[0];
  if (recentAce) {
    const member = eligible.find((m) => m.id === recentAce.member_id);
    if (member) {
      const course = recentAce.course_id ? (courseNames[recentAce.course_id] || '').replace(/^ANCC /, '') : 'ANCC';
      return {
        member,
        reason: 'ACE',
        headline: `Aced hole ${recentAce.hole_num}`,
        subline: `${course || 'Club'} · Hole-in-one registry`,
      };
    }
  }

  const newMembers = eligible.filter((m) => new Date(m.created_at).getTime() >= twoWeeksAgo);
  if (newMembers.length) {
    const newest = [...newMembers].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    return {
      member: newest,
      reason: 'NEW',
      headline: 'New to the club',
      subline: `Joined ${new Date(newest.created_at).toLocaleDateString([], { month: 'long', day: 'numeric' })}`,
    };
  }

  const weekRounds = posts.filter(
    (p) => p.type === 'round'
      && new Date(p.created_at).getTime() >= weekAgo
      && p.author_id !== meId
      && p.round?.gross_total != null
      && p.round.holes === 'full',
  );
  if (weekRounds.length) {
    const best = weekRounds.reduce<{ post: PostEnriched; diff: number } | null>((b, p) => {
      const par = p.round?.course?.par ?? 72;
      const diff = p.round!.gross_total! - par;
      if (!b || diff < b.diff) return { post: p, diff };
      return b;
    }, null);
    if (best) {
      const member = eligible.find((m) => m.id === best.post.author_id);
      if (member) {
        const course = (best.post.round?.course?.name || courseNames[best.post.round?.course_id || ''] || 'the club').split(' — ')[0];
        return {
          member,
          reason: 'HOT',
          headline: `${best.post.round!.gross_total} at ${course}`,
          subline: best.diff < 0
            ? `${Math.abs(best.diff)} under par · best this week at the club`
            : best.diff === 0
              ? 'Even par · best this week at the club'
              : `+${best.diff} · low score this week`,
        };
      }
    }
  }

  const weekAchievements = posts.filter(
    (p) => p.type === 'achievement' && new Date(p.created_at).getTime() >= weekAgo && p.author_id !== meId,
  );
  const achCounts: Record<string, number> = {};
  weekAchievements.forEach((p) => { achCounts[p.author_id] = (achCounts[p.author_id] || 0) + 1; });
  const topAchiever = Object.entries(achCounts).sort((a, b) => b[1] - a[1])[0];
  if (topAchiever && topAchiever[1] >= 2) {
    const member = eligible.find((m) => m.id === topAchiever[0]);
    if (member) {
      return {
        member,
        reason: 'STREAK',
        headline: `${topAchiever[1]} achievements this week`,
        subline: 'On a tear lately',
      };
    }
  }

  const roundCounts: Record<string, number> = {};
  posts.filter((p) => p.type === 'round' && new Date(p.created_at).getTime() >= weekAgo && p.author_id !== meId)
    .forEach((p) => { roundCounts[p.author_id] = (roundCounts[p.author_id] || 0) + 1; });
  const topPlayer = Object.entries(roundCounts).sort((a, b) => b[1] - a[1])[0];
  if (topPlayer && topPlayer[1] >= 3) {
    const member = eligible.find((m) => m.id === topPlayer[0]);
    if (member) {
      return {
        member,
        reason: 'GRINDER',
        headline: `${topPlayer[1]} rounds this week`,
        subline: 'Logging serious time at the club',
      };
    }
  }

  const start = new Date(new Date().getFullYear(), 0, 1);
  const weekOfYear = Math.floor(((Date.now() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
  const yearKey = new Date().getFullYear() * 100 + weekOfYear;
  const member = eligible[yearKey % eligible.length];
  return {
    member,
    reason: 'FEATURED',
    headline: 'Member spotlight',
    subline: member.name,
  };
}

export function MemberSpotlight({ members, posts, aces, me, following, courseNames, onOpenProfile }: Props) {
  const pick = useMemo(
    () => pickSpotlight(members, posts, aces, me.id, courseNames),
    [members, posts, aces, me.id, courseNames],
  );

  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    if (pick) setIsFollowing(following.includes(pick.member.id));
  }, [following, pick?.member.id]);

  if (!pick) return null;

  const rc = REASON_CONFIG[pick.reason];
  const issueNumber = Math.floor((Date.now() - new Date('2024-01-01').getTime()) / (7 * 86400000));

  const memberRounds = posts.filter((p) => p.author_id === pick.member.id && p.type === 'round');
  const memberAces = aces.filter((a) => a.member_id === pick.member.id);
  const bestRound = memberRounds.reduce<{ score: number; diff: number } | null>((best, p) => {
    const score = p.round?.gross_total;
    const par = p.round?.course?.par;
    if (score == null || par == null) return best;
    const diff = score - par;
    if (!best || diff < best.diff) return { score, diff };
    return best;
  }, null);

  const toggleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (followBusy) return;
    setFollowBusy(true);
    const memberId = pick.member.id;
    try {
      if (isFollowing) {
        await db.follows.remove(memberId);
        setIsFollowing(false);
        notifyFollowingChanged(memberId, false);
      } else {
        await db.follows.add(memberId);
        setIsFollowing(true);
        notifyFollowingChanged(memberId, true);
      }
    } finally {
      setFollowBusy(false);
    }
  };

  return (
    <div
      className="relative mb-3.5 overflow-hidden rounded-xl border border-navy shadow-[0_4px_14px_rgba(10,30,63,0.18),0_1px_2px_rgba(10,30,63,0.1)]"
      style={{ background: 'linear-gradient(135deg, #f9f5e8 0%, #f4ecd4 100%)' }}
    >
      <div className="pointer-events-none absolute inset-[5px] rounded-[9px] border" style={{ borderColor: rc.accent, background: rc.bgTint }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #0a1e3f 0.5px, transparent 0.5px)', backgroundSize: '6px 6px' }} />

      <div className="spotlight-stamp absolute right-2.5 top-2.5 z-10 flex h-9 w-9 items-center justify-center rounded-full text-sm shadow-md" style={{ background: `radial-gradient(circle at 30% 30%, ${rc.accent} 0%, #8a6d3d 70%, #0a1e3f 100%)`, transform: 'rotate(-2deg)' }}>
        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-navy/60">{rc.stamp}</span>
      </div>

      <button type="button" onClick={() => onOpenProfile(pick.member.id)} className="relative z-[1] w-full px-4 pb-3 pt-3.5 text-left">
        <div className="mb-3 flex items-center justify-between border-b border-dashed border-navy/20 pb-2 pr-10">
          <div className="text-[9px] font-extrabold tracking-[0.25em] text-navy">THE EAGLE</div>
          <div className="text-[9px] font-bold italic tracking-wider text-ink-soft">NO. {issueNumber} · {new Date().getFullYear()}</div>
        </div>

        <div className="mb-3.5 flex items-start gap-3.5">
          <div className="spotlight-shimmer shrink-0 rounded-full p-[3px]" style={{ background: `linear-gradient(135deg, ${rc.accent}, #8a6d3d, ${rc.accent})`, backgroundSize: '200% 200%' }}>
            <div className="rounded-full bg-[#f9f5e8] p-0.5">
              <Avatar member={pick.member} size={64} />
            </div>
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="mb-1.5 inline-block rounded bg-navy px-2 py-0.5 text-[9px] font-extrabold tracking-wider" style={{ color: rc.accent }}>
              {rc.badge}
            </div>
            <div className="font-serif text-[22px] font-extrabold leading-none text-navy">{pick.member.name}</div>
            {pick.member.handicap != null && (
              <div className="mt-1 text-[11px] font-semibold text-ink-soft">HCP {pick.member.handicap}</div>
            )}
          </div>
        </div>

        <div className="spotlight-tick mb-3 rounded-md bg-navy px-3 py-2.5 text-cream">
          <div className="font-serif text-[15px] font-bold" style={{ color: rc.accent }}>{pick.headline}</div>
          <div className="mt-0.5 text-[11px] italic text-white/70">{pick.subline}</div>
        </div>

        <div className="grid grid-cols-4 rounded-md border border-navy/10 bg-navy/[0.04] py-2.5">
          {[
            { label: 'HCP', value: pick.member.handicap != null ? pick.member.handicap.toFixed(1) : '—' },
            { label: 'ROUNDS', value: String(memberRounds.length) },
            { label: 'BEST', value: bestRound ? String(bestRound.score) : '—' },
            { label: 'ACES', value: String(memberAces.length), accent: memberAces.length > 0 },
          ].map((stat, i) => (
            <div key={stat.label} className={`spotlight-tick text-center ${i < 3 ? 'border-r border-navy/10' : ''}`} style={{ animationDelay: `${0.05 * (i + 1)}s` }}>
              <div className="text-[8px] font-extrabold tracking-widest text-ink-soft">{stat.label}</div>
              <div className={`font-serif text-lg font-extrabold leading-none ${stat.accent ? '' : 'text-navy'}`} style={stat.accent ? { color: rc.accent } : undefined}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-2 flex items-center justify-between border-t border-dashed border-navy/15 pt-1.5 text-[8.5px] italic tracking-wider text-ink-soft">
          <span>· EST. 2026 ·</span>
          <span>Tap card for full profile →</span>
        </div>
      </button>

      <div className="relative z-[2] px-4 pb-3">
        <button
          type="button"
          disabled={followBusy}
          onClick={toggleFollow}
          className={`w-full rounded-md py-2 text-[11px] font-extrabold tracking-widest disabled:opacity-60 ${isFollowing ? 'border border-navy/20 bg-paper text-navy' : 'bg-navy'}`}
          style={isFollowing ? undefined : { color: rc.accent }}
        >
          {followBusy ? '…' : isFollowing ? `FOLLOWING ${pick.member.name.split(' ')[0].toUpperCase()}` : `+ FOLLOW ${pick.member.name.split(' ')[0].toUpperCase()}`}
        </button>
      </div>
    </div>
  );
}
